<template>
  <v-content>
    <v-dialog
      v-model="ipAddressListDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="700"
      @keydown.esc="ipAddressListDialogOpened = false"
    >
      <v-card block flat v-if="currentUserFirm">
        <v-card-title class="title pb-0">
          Manage IP addresses
          <v-spacer />
          <v-btn
            fab
            small
            class="error--text text-none my-0"
            flat
            @click="ipAddressListDialogOpened = false"
          >
            <v-icon>clear</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="text-xs-left subheading">
          <v-alert type="info" :value="true" outline>
            Only the IP Addresses below will be allowed to access the remote
            desktop that contains the firm's tax software.
          </v-alert>
          <v-layout column>
            <div class="mt-2 darkgrey--text">Allowed IP Addresses:</div>
            <v-layout
              align-center
              class="my-2 ml-2"
              v-for="(ipAddress, i) in currentUserFirm.approved_ip_addresses"
              :key="i"
            >
              {{ ipAddress }}
              <v-icon
                @click="removeIPAddress(ipAddress)"
                class="ml-2 error--text"
                size="15"
                style="cursor: pointer"
                >clear</v-icon
              >
            </v-layout>
            <span
              v-if="isEmpty(currentUserFirm.approved_ip_addresses)"
              class="my-2 grey--text ml-2"
              >None</span
            >
          </v-layout>
          <v-layout align-center>
            <v-text-field
              v-model="newIPAddress"
              hide-details
              class="default-input"
              solo
              placeholder="12.345.6.78"
            />
            <v-btn class="primary text-none" @click="addIPAddress">
              Add IP Address
            </v-btn>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="requestPasswordDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="700"
      @keydown.esc="requestPasswordDialogOpened = false"
    >
      <v-card block flat>
        <v-card-title class="title pb-0">
          Request Password Change
        </v-card-title>
        <v-card-text class="text-xs-left subheading">
          <v-alert type="info" :value="true" outline class="mb-4">
            A password change request can take up to 1 business day to complete.
          </v-alert>
          <v-layout column>
            <div class="darkgrey--text">
              New RDP password
              <v-tooltip bottom>
                <v-icon size="16" slot="activator">information</v-icon>
                <span>the password used to access the remote desktop</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="rdpPassword"
              class="default-input"
              solo
              placeholder="Enter New Password"
            />
            <div class="darkgrey--text">
              New Tax Software password
              <v-tooltip bottom>
                <v-icon size="16" slot="activator">information</v-icon>
                <span>the password used to access the tax software</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="taxSoftwarePassword"
              class="default-input"
              solo
              placeholder="Enter New Password"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="default-input primary text-none"
            @click="updatePasswords()"
            :disabled="isEmpty(rdpPassword) && isEmpty(taxSoftwarePassword)"
          >
            Change Password
          </v-btn>
          <v-btn
            outline
            class="default-input error--text text-none"
            @click="requestPasswordDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div id="recaptcha-container" />
    <v-container justify-center class="settings__container">
      <v-layout class="mb-2" align-center>
        <soraban-action-button
          @click="$router.push('/overview')"
          icon="keyboard_arrow_left"
          help="Back"
        />
        <h1>My Cloud</h1>
        <v-spacer />
      </v-layout>
      <v-card flat class="content-container">
        <v-card-text>
          <v-layout class="text-xs-left" wrap v-if="currentUserFirm">
            <v-flex xs12 sm6 class="px-2">
              <div>
                Number of users
                <v-tooltip bottom>
                  <v-icon slot="activator" size="15" class="ml-1">
                    information
                  </v-icon>
                  <span
                    >Number of concurrent users that can remotely access the
                    cloud-hosted desktop</span
                  >
                </v-tooltip>
              </div>
              <v-text-field
                type="text"
                placeholder="3"
                v-model="currentUserFirm.cloud_access_users"
                required
                :rules="fieldRules"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs12 sm6 class="px-2 text-xs-left">
              <div>
                Windows Version
                <v-tooltip bottom>
                  <v-icon slot="activator" size="15" class="ml-1">
                    information
                  </v-icon>
                  <span
                    >Please contact us if you want to upgrade to a new windows
                    version</span
                  >
                </v-tooltip>
              </div>
              <v-select
                disabled
                :value="'Windows 10'"
                solo
                hide-details
                :items="['Windows 10']"
                class="default-input"
              />
            </v-flex>
          </v-layout>
          <v-layout
            justify-center
            align-center
            class="text-xs-left"
            wrap
            v-if="currentUserFirm"
          >
            <v-flex xs12 sm6 class="px-2">
              <v-layout> RAM Size (GB) </v-layout>
              <v-select
                solo
                :items="ramOptions"
                placeholder="16"
                v-model="currentUserFirm.ram_size"
                class="default-input"
              />
            </v-flex>

            <v-flex xs12 sm6 class="px-2">
              <div>SSD Storage Size (GB)</div>
              <v-text-field
                solo
                placeholder="100"
                v-model="currentUserFirm.storage_size"
                class="default-input"
              />
            </v-flex>
          </v-layout>

          <v-layout
            justify-start
            align-center
            class="text-xs-left mb-4"
            wrap
            v-if="currentUserFirm"
          >
            <v-flex xs12 sm6 class="px-2 text-xs-left">
              <div>Backup Plan</div>
              <v-select
                v-model="currentUserFirm.backup_plan"
                solo
                hide-details
                :items="backupPlans"
                class="default-input"
              />
            </v-flex>
            <v-flex xs6 class="px-2">
              <div class="text-xs-left">Require 2FA</div>
              <v-switch
                value="true"
                hide-details
                class="default-input mt-0 pt-0"
              />
            </v-flex>

            <!-- NOTE @chris IP whitelist a possible future feature. -->
            <!-- <v-flex xs6 sm3 class="px-2">
              <div>
                <span v-if="currentUser.enabled_2fa" class="error--text"
                  >*</span
                >
                IP Whitelist
              </div>
              <v-btn
                class="primary--text text-capitalize ml-0"
                outline
                @click="openIPAddressListDialog"
              >
                Edit Access
              </v-btn>
            </v-flex> -->

            <v-flex xs6 sm3 class="px-2">
              <div>
                <span class="error--text">*</span>
                Password
              </div>
              <v-btn
                class="primary--text text-capitalize ml-0"
                outline
                @click="openRequestPasswordDialog"
              >
                Edit Password
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-layout align-center class="text-xs-center">
            <v-spacer class="hidden-xs-only" />
            <v-btn
              outline
              class="primary--text text-capitalize mr-2"
              id="reset-password-button"
              @click="resetToDefault"
            >
              Reset to default
            </v-btn>
            <v-btn
              flat
              id="save-changes-button"
              class="primary text-capitalize ml-2"
              @click="saveMyCloud"
              :disabled="!myCloudSavable"
            >
              <v-icon> check </v-icon>
              Save Changes
            </v-btn>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-container>
  </v-content>
</template>

<script>
import { ref, reactive, computed, onMounted } from '@vue/composition-api';
import isEmpty from 'lodash/isEmpty';
import 'firebase/auth';
import API from '@/api';

export default {
  setup(_, { root }) {
    let currentUserFirm = ref(null);
    const fieldRules = ref([v => !!v || 'This field is required']);
    let firmUsers = ref([]);
    const phoneRules = ref([
      v => !!v || 'Required for Two-factor authentication',
    ]);
    const ramOptions = ref([2, 4, 8, 16, 32]);
    let newIPAddress = ref('');
    let ipAddressListDialogOpened = ref(false);
    let rdpPassword = ref('');
    let taxSoftwarePassword = ref('');
    let requestPasswordDialogOpened = ref(false);
    const backupPlans = ref([
      'None',
      'Daily with 30 Day-Recovery',
      'Daily with 60 Day-Recovery',
      'Weekly with 30 Day-Recovery',
      'Weekly with 60 Day-Recovery',
      'Weekly with 6 Month-Recovery',
      'Monthly with 12 Month-Recovery',
    ]);

    const myCloudSavable = computed(() => {
      return (
        currentUserFirm.value &&
        currentUserFirm.value.cloud_access_users &&
        currentUserFirm.value.storage_size
      );
    });

    const store = computed(() => root.$store).value;
    const currentUser = store.getters.user;
    const isAdvisor = store.getters.isAdvisor;

    async function getCurrentUserFirm() {
      const response = await API.firm.getCurrentUserFirm();
      currentUserFirm.value = response.data;
    }

    async function saveMyCloud() {
      const response = await API.integration.saveMyCloud({
        ramSize: currentUserFirm.value.ram_size,
        storageSize: currentUserFirm.value.storage_size,
        cloudAccessUsers: currentUserFirm.value.cloud_access_users,
        backupPlan: currentUserFirm.value.backup_plan,
      });
      if (response.data.error) {
        store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        store.dispatch('application/setSuccessNotification', {
          message: 'The saved changes will take effect in one business day.',
        });
      }
    }

    function resetToDefault() {
      currentUserFirm.value.ram_size = 16;
      currentUserFirm.value.backup_plan = 'Daily with 30 Day-Recovery';
      currentUserFirm.value.storage_size = 100;
      currentUserFirm.value.cloud_access_users = 3;
    }
    async function updatePasswords() {
      try {
        const response = await API.integration.updateTaxSoftwarePasswords({
          rdpPassword: rdpPassword.value,
          taxSoftwarePassword: taxSoftwarePassword.value,
        });
        if (response.data.error) {
          store.dispatch('application/setErrorNotification', {
            message: 'There was a problem changing the password/s.',
          });
        } else {
          store.dispatch('application/setSuccessNotification', {
            message:
              'The password will be changed in few hours. You will be notified by email once the password/s has been changed.',
          });
        }
      } catch (e) {
        store.dispatch('application/setErrorNotification', {
          message: 'There was a problem changing the password/s.',
        });
      } finally {
        requestPasswordDialogOpened.value = false;
      }
    }
    function openIPAddressListDialog() {
      newIPAddress.value = '';
      ipAddressListDialogOpened.value = true;
    }
    async function addIPAddress() {
      if (
        currentUserFirm &&
        currentUserFirm.value.approved_ip_addresses.includes(newIPAddress.value)
      ) {
        store.dispatch('application/setErrorNotification', {
          message: 'IP address is already approved',
        });
        return;
      }
      try {
        const response = await API.integration.saveApprovedIPAddresses({
          ipAddressToAdd: newIPAddress.value,
        });
        if (response.data.error) {
          store.dispatch('application/setErrorNotification', {
            message: 'There was a problem adding the IP Address.',
          });
        } else {
          store.dispatch('application/setSuccessNotification', {
            message:
              'The IP Address will be added in few hours. You will be notifid once the IP address has been added.',
          });
        }
        newIPAddress.value = '';
      } catch (e) {
        store.dispatch('application/setErrorNotification', {
          message: 'There was a problem adding the IP Address.',
        });
      }
    }
    async function removeIPAddress(ipAddress) {
      try {
        const response = await API.integration.saveApprovedIPAddresses({
          ipAddressToRemove: ipAddress,
        });
        if (response.data.error) {
          $store.dispatch('application/setErrorNotification', {
            message: 'There was a problem removing the IP Address.',
          });
        } else {
          $store.dispatch('application/setSuccessNotification', {
            message: 'The IP Address will be removed in few hours.',
          });
        }
      } catch (e) {
        $store.dispatch('application/setErrorNotification', {
          message: 'There was a problem removing the IP Address.',
        });
      }
    }
    function openRequestPasswordDialog() {
      rdpPassword.value = '';
      taxSoftwarePassword.value = '';
      requestPasswordDialogOpened.value = true;
    }

    // Mounted
    onMounted(async () => {
      try {
        const response = await API.firm.getCurrentUserFirm();

        if (response.data.error) {
          console.log(response.data.error);
        } else {
          currentUserFirm.value = response.data;
          if (!currentUserFirm.value.my_cloud_enabled) {
            $router.push('/overview');
          }
        }
      } catch (e) {
        console.log(e);
      } finally {
        store.commit('application/END_LOADING');
      }
    });

    return {
      fieldRules,
      phoneRules,
      currentUser,
      isAdvisor,
      myCloudSavable,
      currentUserFirm,
      resetToDefault,
      saveMyCloud,
      openIPAddressListDialog,
      addIPAddress,
      rdpPassword,
      taxSoftwarePassword,
      requestPasswordDialogOpened,
      openRequestPasswordDialog,
      ipAddressListDialogOpened,
      isEmpty,
      newIPAddress,
      ramOptions,
      backupPlans,
      updatePasswords,
    };
  },
};
</script>

<style lang="postcss" scoped>
.container {
  max-width: 1000px;
}
.settings__container {
  max-width: 700px;
}
.google-sign-in {
  cursor: pointer;
}
</style>
